import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, List, Map as MapIcon, MapPin, ChevronRight, ExternalLink, Loader2, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import TrailerModal from './TrailerModal';
import FiltersPanel from './FiltersPanel';
import CinemaMap from './CinemaMap';
import { MenuButton } from './NavigationMenu';
import ShareButton from './ShareButton';
import NewsletterForm from './NewsletterForm';
import { trackEvent } from './MetaPixel';
import { movieApi, mediaApi, cinemasApi } from '../services/api';

// Generate dates for the next 7 days (fallback)
const generateDates = () => {
  const dates = [];
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    dates.push({
      date: date.toISOString().split('T')[0],
      day: days[date.getDay()],
      month: months[date.getMonth()],
      dayNum: String(date.getDate()).padStart(2, '0'),
      fullDate: date.toISOString().split('T')[0]
    });
  }
  return dates;
};

// Extract unique dates from cinema sessions
const extractDatesFromCinemas = (cinemas) => {
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  
  const uniqueDates = new Set();
  
  cinemas.forEach(cinema => {
    // Include dates from showtimes
    (cinema.showtimes || []).forEach(showtime => {
      if (showtime.date) {
        uniqueDates.add(showtime.date);
      }
    });
  });
  
  // Convert to array and sort
  const sortedDates = Array.from(uniqueDates).sort();
  
  // Convert to date objects with display info
  return sortedDates.map(dateStr => {
    const date = new Date(dateStr + 'T12:00:00');
    return {
      date: dateStr,
      day: days[date.getDay()],
      month: months[date.getMonth()],
      dayNum: String(date.getDate()).padStart(2, '0'),
      fullDate: dateStr
    };
  });
};

// Calculate distance between two points in km
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Format distance for display
const formatDistance = (km) => {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
};

const TicketsPage = () => {
  const [searchCity, setSearchCity] = useState('');
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showMap, setShowMap] = useState(false); // Will be set to true for desktop after mount
  const [showTrailer, setShowTrailer] = useState(false);
  const [filters, setFilters] = useState({ time: 'any', version: 'all' });
  
  const [movie, setMovie] = useState(null);
  const [media, setMedia] = useState(null);
  const [cinemas, setCinemas] = useState([]);
  const [filteredCinemas, setFilteredCinemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [selectedCinema, setSelectedCinema] = useState(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect desktop and set map visible by default
  useEffect(() => {
    const checkDesktop = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setShowMap(true);
      }
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Function to request geolocation
  const requestGeolocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserLocation(loc);
          setSearchLocation(loc);
          setGeoLoading(false);
          
          // Reverse geocode to get city name
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${loc.lat}&lon=${loc.lng}`)
            .then(res => res.json())
            .then(data => {
              const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || 'Tu ubicación';
              setLocationName(city);
              setSearchCity(city);
            })
            .catch(() => {
              setLocationName('Tu ubicación');
              setSearchCity('');
            });
        },
        (error) => {
          console.log('Geolocation error:', error.message);
          // Default to Madrid if geolocation fails
          const defaultLoc = { lat: 40.4168, lng: -3.7038 };
          setUserLocation(defaultLoc);
          setSearchLocation(defaultLoc);
          setLocationName('Madrid (ubicación por defecto)');
          setSearchCity('Madrid');
          setGeoLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    } else {
      const defaultLoc = { lat: 40.4168, lng: -3.7038 };
      setUserLocation(defaultLoc);
      setSearchLocation(defaultLoc);
      setLocationName('Madrid');
      setSearchCity('Madrid');
      setGeoLoading(false);
    }
  }, []);

  // Get user's geolocation on mount
  useEffect(() => {
    requestGeolocation();
  }, [requestGeolocation]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [movieRes, mediaRes, cinemasRes] = await Promise.all([
          movieApi.get(), mediaApi.get(), cinemasApi.getAll()
        ]);
        setMovie(movieRes.data);
        setMedia(mediaRes.data);
        setCinemas(cinemasRes.data);
        
        // Extract dates from cinema sessions
        const extractedDates = extractDatesFromCinemas(cinemasRes.data);
        if (extractedDates.length > 0) {
          setDates(extractedDates);
          setSelectedDate(extractedDates[0].fullDate);
        } else {
          // Fallback to generated dates if no sessions
          const fallbackDates = generateDates();
          setDates(fallbackDates);
          setSelectedDate(fallbackDates[0]?.fullDate || '');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback dates on error
        const fallbackDates = generateDates();
        setDates(fallbackDates);
        setSelectedDate(fallbackDates[0]?.fullDate || '');
      } finally {
        setLoading(false);
      }
    };
    
    if (!geoLoading) fetchData();
  }, [geoLoading]);

  // Filter and sort cinemas when location or filters change
  useEffect(() => {
    if (!searchLocation || cinemas.length === 0) return;
    
    let filtered = cinemas.map(cinema => {
      const coords = cinema.coordinates || { lat: 40.4168, lng: -3.7038 };
      const distance = calculateDistance(searchLocation.lat, searchLocation.lng, coords.lat, coords.lng);
      
      // If cinema is in ticket-only mode, don't filter showtimes
      if (cinema.ticketOnlyMode && cinema.ticketUrl) {
        return {
          ...cinema,
          distance: distance,
          formattedDistance: formatDistance(distance),
          showtimes: [] // No individual showtimes needed
        };
      }
      
      // Filter showtimes by selected date and version filter
      let showtimes = cinema.showtimes || [];
      
      // Filter by date
      if (selectedDate) {
        showtimes = showtimes.filter(s => !s.date || s.date === selectedDate);
      }
      
      // Filter by version
      if (filters.version !== 'all') {
        const versionMap = { 'dubbed': 'Doblada', 'subtitled': 'VOSE' };
        showtimes = showtimes.filter(s => s.format === versionMap[filters.version]);
      }
      
      // Filter by time
      if (filters.time !== 'any') {
        showtimes = showtimes.filter(s => {
          if (!s.time) return true; // Keep sessions without time
          const hour = parseInt(s.time.split(':')[0]);
          switch(filters.time) {
            case 'morning': return hour < 14;
            case 'afternoon': return hour >= 14 && hour < 19;
            case 'evening': return hour >= 19;
            default: return true;
          }
        });
      }
      
      return {
        ...cinema,
        distance: distance,
        formattedDistance: formatDistance(distance),
        showtimes: showtimes
      };
    });
    
    // Show cinemas with showtimes OR in ticket-only mode
    filtered = filtered.filter(c => 
      (c.ticketOnlyMode && c.ticketUrl) || 
      (c.showtimes && c.showtimes.length > 0)
    );
    
    // Sort by distance
    filtered.sort((a, b) => a.distance - b.distance);
    
    setFilteredCinemas(filtered);
  }, [searchLocation, cinemas, selectedDate, filters]);

  // Search for city
  const handleCitySearch = useCallback(async () => {
    if (!searchCity.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchCity)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const newLocation = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        setSearchLocation(newLocation);
        setSelectedCinema(null); // Clear selected cinema so map flies to the new city
        setLocationName(data[0].display_name.split(',')[0]);
      }
    } catch (error) {
      console.error('Error searching city:', error);
    } finally {
      setSearching(false);
    }
  }, [searchCity]);

  // Handle Enter key in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCitySearch();
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading || geoLoading) {
    return (
      <div className="min-h-screen bg-[#1a2b3c] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#50c878] mx-auto mb-4" />
          <p className="text-[#f5f5dc]/60">Geolocalizando y buscando cines cercanos...</p>
        </div>
      </div>
    );
  }

  const posterUrl = media?.posterUrl || 'https://customer-assets.emergentagent.com/job_3c846719-8635-42c0-b5a0-5b54d18801eb/artifacts/dzlp4fqx_cartel_web_legendarios.jpg';
  const posterMobileUrl = media?.posterMobileUrl || posterUrl;

  return (
    <div className="min-h-screen bg-[#1a2b3c] text-[#f5f5dc] font-sans w-full max-w-full overflow-x-hidden">

      {/* Desktop Header */}
      <header className="hidden lg:block fixed top-0 left-0 right-0 z-50 bg-[#1a2b3c] border-b border-[#50c878]/20">
        <div className="flex items-center justify-between px-6 py-4">
          <MenuButton />
          <div className="flex-1" />
          <ShareButton />
        </div>
      </header>

      {/* Desktop Layout - Fixed poster + Scrollable content */}
      <div className="hidden lg:flex min-h-screen pt-[72px] w-full">
        {/* Left Sidebar - Movie Poster (35% width) - FIXED */}
        <aside className="fixed left-0 top-[72px] w-[35%] h-[calc(100vh-72px)] z-10">
          <div className="h-full overflow-hidden cursor-pointer" onClick={() => setShowTrailer(true)}>
            <img 
              src={posterUrl}
              alt="Movie Poster"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute top-0 right-0 w-16 h-full bg-gradient-to-l from-[#1a2b3c] to-transparent" />
          </div>
        </aside>

        {/* Right Content Area - SCROLLABLE */}
        <main className="ml-[35%] flex-1 bg-[#1a2b3c] h-[calc(100vh-72px)] overflow-y-auto">
          {/* Search Bar */}
          <div className="p-5 bg-[#2e4a2a] border-b border-[#50c878]/20 w-full sticky top-0 z-20">
            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 relative min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#50c878]" />
                <Input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full pl-10 pr-3 py-2.5 text-base bg-[#1a2b3c] border-[#50c878]/30 text-[#f5f5dc] placeholder:text-[#f5f5dc]/40 focus:border-[#50c878] rounded-lg"
                  placeholder="Ciudad..."
                />
              </div>
              
              <Button
                onClick={handleCitySearch}
                disabled={searching}
                className="px-4 py-2.5 bg-[#50c878] text-white hover:bg-[#40a0a0] text-sm flex-shrink-0 rounded-lg whitespace-nowrap"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2.5 border-[#50c878]/40 text-[#f5f5dc] hover:bg-[#50c878]/20 hover:border-[#50c878] rounded-lg flex-shrink-0 whitespace-nowrap"
              >
                Filtros
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Location indicator */}
            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-[#f5f5dc]/50 min-w-0">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Cerca de: <strong className="text-[#f5f5dc]">{locationName}</strong></span>
              </div>
              <button 
                onClick={requestGeolocation}
                className="flex-shrink-0 text-[#50c878] hover:text-[#f5f5dc] transition-colors ml-2"
              >
                Usar mi ubicación
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <FiltersPanel filters={filters} setFilters={setFilters} onClose={() => setShowFilters(false)} />
          )}

          {/* Date Selector */}
          <div className="bg-[#2e4a2a] border-b border-[#50c878]/20 w-full">
            <div className="overflow-x-auto scrollbar-hide w-full">
              <div className="flex items-center gap-2 px-3 py-3 w-max">
                {dates.map((dateObj) => (
                  <button
                    key={dateObj.date}
                    onClick={() => setSelectedDate(dateObj.fullDate)}
                    className={`flex flex-col items-center px-4 py-2 rounded-lg min-w-[60px] flex-shrink-0 transition-all ${
                      selectedDate === dateObj.fullDate 
                        ? 'bg-[#50c878] text-white' 
                        : 'bg-[#1a2b3c] text-[#f5f5dc]/60 hover:bg-[#50c878]/20 hover:text-[#f5f5dc]'
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-wider">{dateObj.month}</span>
                    <span className="text-xl font-bold">{dateObj.dayNum}</span>
                    <span className="text-[10px]">{dateObj.day}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Map Section - Always visible on desktop */}
          {showMap && (
            <div className="h-[300px] border-b border-[#50c878]/20">
              <CinemaMap 
                cinemas={filteredCinemas} 
                userLocation={searchLocation}
                selectedCinema={selectedCinema}
                onSelectCinema={setSelectedCinema}
                movieTitle={movie?.title}
              />
            </div>
          )}

          {/* Results Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#50c878]/10 bg-[#1a2b3c]">
            <p className="text-[#f5f5dc]/50 text-sm">
              {Math.min(filteredCinemas.length, 3)} de {filteredCinemas.length} cines
            </p>
            <button
              onClick={() => setShowMap(!showMap)}
              className={`p-2 rounded ${showMap ? 'bg-[#50c878]/30 text-[#f5f5dc]' : 'text-[#f5f5dc]/40 hover:bg-[#50c878]/10'}`}
            >
              <MapIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Cinema Listings - Show only top 3 on desktop */}
          <div className="divide-y divide-[#50c878]/10 w-full">
            {filteredCinemas.length === 0 ? (
              <div className="p-8 text-center text-[#f5f5dc]/50">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No se encontraron cines con pases disponibles para esta fecha y filtros.</p>
              </div>
            ) : (
              filteredCinemas.slice(0, 3).map((cinema) => (
                <CinemaListItem 
                  key={cinema.id} 
                  cinema={cinema} 
                  movieTitle={movie?.title}
                  isSelected={selectedCinema?.id === cinema.id}
                  onClick={() => setSelectedCinema(cinema)}
                />
              ))
            )}
          </div>

          {/* More Cinemas Section - Show next 5 */}
          {filteredCinemas.length > 3 && (
            <div className="bg-[#0d1620] border-t border-[#50c878]/20 p-4">
              <p className="text-[#f5f5dc]/60 text-sm mb-3">Más cines cercanos</p>
              <div className="flex flex-wrap gap-2">
                {filteredCinemas.slice(3, 8).map((cinema) => (
                  <button
                    key={cinema.id}
                    onClick={() => {
                      setSelectedCinema(cinema);
                      // Scroll to top smoothly
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-[#1a2b3c] rounded-lg text-sm text-[#f5f5dc]/70 hover:bg-[#50c878]/20 hover:text-[#f5f5dc] transition-colors border border-[#50c878]/20"
                  >
                    <span className="truncate max-w-[120px]">{cinema.name}</span>
                    <span className="text-[#50c878] text-xs whitespace-nowrap">
                      {cinema.distance ? formatDistance(cinema.distance) : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop Footer */}
          <footer className="bg-[#0d1620] border-t border-[#50c878]/20 p-6">
            {/* Newsletter Section */}
            <div className="mb-6">
              <NewsletterForm variant="compact" />
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="border border-[#f5f5dc]/40 px-2 py-1">
                <p className="text-[#f5f5dc]/80 text-xs font-bold">+7</p>
              </div>
              <p className="text-[#f5f5dc]/50 text-xs">NO RECOMENDADA PARA MENORES DE 7 AÑOS</p>
            </div>
            
            <div className="flex items-center justify-center gap-4 text-[#f5f5dc]/40 text-xs mb-4">
              <Link to="/aviso-legal" className="hover:text-[#f5f5dc]">Aviso Legal</Link>
              <span>•</span>
              <Link to="/politica-privacidad" className="hover:text-[#f5f5dc]">Privacidad</Link>
              <span>•</span>
              <Link to="/politica-cookies" className="hover:text-[#f5f5dc]">Cookies</Link>
            </div>
            
            <p className="text-[#f5f5dc]/30 text-xs text-center">
              © 2026 VERSIÓN DIGITAL
            </p>
          </footer>
        </main>
      </div>

      {/* Mobile Layout - Original scrolling behavior */}
      <div className="lg:hidden flex flex-col min-h-screen w-full">
        {/* Mobile Hero - Poster at top */}
        <div className="relative w-full">
          {/* Hero Image */}
          <div className="relative h-56 w-full overflow-hidden">
            <img 
              src={posterMobileUrl}
              alt="Movie Poster"
              className="w-full h-full object-cover object-top"
              onClick={() => setShowTrailer(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2b3c] via-transparent to-transparent" />
          </div>
          
          {/* Header buttons overlay */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 pt-8 safe-area-inset">
            <MenuButton />
            <ShareButton className="bg-black/30 backdrop-blur-sm px-3 py-2 rounded-full text-sm text-white/90 hover:text-white" />
          </div>
        </div>

        {/* Mobile Main Content */}
        <main className="flex-1 bg-[#1a2b3c] flex flex-col w-full min-w-0">
          {/* Mobile Search Bar */}
          <div className="p-3 bg-[#2e4a2a] border-b border-[#50c878]/20 w-full">
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1 relative min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#50c878]" />
                <Input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full pl-10 pr-2 py-2.5 text-base bg-[#1a2b3c] border-[#50c878]/30 text-[#f5f5dc] placeholder:text-[#f5f5dc]/40 focus:border-[#50c878] rounded-lg"
                  placeholder="Ciudad..."
                />
              </div>
              
              <Button
                onClick={handleCitySearch}
                disabled={searching}
                className="px-3 py-2.5 bg-[#50c878] text-white hover:bg-[#40a0a0] text-sm flex-shrink-0 rounded-lg"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center p-2.5 border-[#50c878]/40 text-[#f5f5dc] hover:bg-[#50c878]/20 rounded-lg flex-shrink-0"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-[#f5f5dc]/50 min-w-0">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Cerca de: <strong className="text-[#f5f5dc]">{locationName}</strong></span>
              </div>
              <button 
                onClick={requestGeolocation}
                className="flex-shrink-0 text-[#50c878] hover:text-[#f5f5dc] transition-colors ml-2"
              >
                Usar mi ubicación
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <FiltersPanel filters={filters} setFilters={setFilters} onClose={() => setShowFilters(false)} />
          )}

          {/* Date Selector - Horizontal scroll */}
          <div className="bg-[#2e4a2a] border-b border-[#50c878]/20 w-full">
            <div className="overflow-x-auto scrollbar-hide w-full -webkit-overflow-scrolling-touch">
              <div className="flex items-center gap-2 px-3 py-3 w-max">
                {dates.map((dateObj) => (
                  <button
                    key={dateObj.date}
                    onClick={() => setSelectedDate(dateObj.fullDate)}
                    className={`flex flex-col items-center px-4 py-2 rounded-lg min-w-[60px] flex-shrink-0 transition-all ${
                      selectedDate === dateObj.fullDate 
                        ? 'bg-[#50c878] text-white' 
                        : 'bg-[#1a2b3c] text-[#f5f5dc]/60 hover:bg-[#50c878]/20 hover:text-[#f5f5dc]'
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-wider">{dateObj.month}</span>
                    <span className="text-xl font-bold">{dateObj.dayNum}</span>
                    <span className="text-[10px]">{dateObj.day}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* View Toggle & Results Header */}
          <div className="px-3 py-2 flex items-center justify-between border-b border-[#50c878]/10 bg-[#1a2b3c]">
            <p className="text-[#f5f5dc]/50 text-xs">
              {Math.min(filteredCinemas.length, 5)} de {filteredCinemas.length} cines
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#50c878]/30 text-[#f5f5dc]' : 'text-[#f5f5dc]/40 hover:bg-[#50c878]/10'}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded ${viewMode === 'map' ? 'bg-[#50c878]/30 text-[#f5f5dc]' : 'text-[#f5f5dc]/40 hover:bg-[#50c878]/10'}`}
              >
                <MapIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 w-full overflow-hidden">
            {viewMode === 'map' && (
              <div className="h-[300px]">
                <CinemaMap 
                  cinemas={filteredCinemas} 
                  userLocation={searchLocation}
                  selectedCinema={selectedCinema}
                  onSelectCinema={setSelectedCinema}
                  movieTitle={movie?.title}
                />
              </div>
            )}

            {/* Cinema Listings - Show top 5 on mobile */}
            <div className="divide-y divide-[#50c878]/10 w-full">
              {filteredCinemas.length === 0 ? (
                <div className="p-8 text-center text-[#f5f5dc]/50">
                  <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No se encontraron cines con pases disponibles para esta fecha y filtros.</p>
                </div>
              ) : (
                filteredCinemas.slice(0, 5).map((cinema) => (
                  <CinemaListItem 
                    key={cinema.id} 
                    cinema={cinema} 
                    movieTitle={movie?.title}
                    isSelected={selectedCinema?.id === cinema.id}
                    onClick={() => setSelectedCinema(cinema)}
                  />
                ))
              )}
            </div>

            {/* More Cinemas Section - Show next 5 */}
            {filteredCinemas.length > 5 && (
              <div className="bg-[#0d1620] border-t border-[#50c878]/20 p-3">
                <p className="text-[#f5f5dc]/60 text-xs mb-2">Más cines cercanos</p>
                <div className="flex flex-wrap gap-1.5">
                  {filteredCinemas.slice(5, 10).map((cinema) => (
                    <button
                      key={cinema.id}
                      onClick={() => {
                        setSelectedCinema(cinema);
                        const url = cinema.ticketUrl || cinema.website || cinema.googleMapsUrl;
                        if (url) {
                          trackEvent('InitiateCheckout', { content_name: cinema.name, content_category: 'ticket_purchase' });
                          window.open(url, '_blank');
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#1a2b3c] hover:bg-[#2e4a2a] border border-[#50c878]/30 rounded-lg text-xs transition-colors"
                    >
                      <span className="text-[#f5f5dc] truncate max-w-[100px]">{cinema.name}</span>
                      <span className="text-[#50c878] whitespace-nowrap">· {cinema.formattedDistance}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Footer */}
          <footer className="mt-auto bg-[#0d1620] border-t border-[#50c878]/20 p-4">
            <div className="mb-4">
              <NewsletterForm variant="compact" />
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="border border-[#f5f5dc]/40 px-2 py-1">
                <p className="text-[#f5f5dc]/80 text-xs font-bold">+7</p>
              </div>
              <p className="text-[#f5f5dc]/50 text-[10px]">NO RECOMENDADA PARA MENORES DE 7 AÑOS</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-[#f5f5dc]/40 text-[10px] mb-3 flex-wrap">
              <Link to="/aviso-legal" className="hover:text-[#f5f5dc]">Aviso Legal</Link>
              <span>•</span>
              <Link to="/politica-privacidad" className="hover:text-[#f5f5dc]">Privacidad</Link>
              <span>•</span>
              <Link to="/politica-cookies" className="hover:text-[#f5f5dc]">Cookies</Link>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-[#f5f5dc]/30 text-[10px]">
                © 2026 VERSIÓN DIGITAL
              </p>
              <button 
                onClick={scrollToTop}
                className="p-1.5 bg-[#50c878]/20 rounded-full hover:bg-[#50c878]/40 transition-colors"
              >
                <ChevronUp className="w-3 h-3 text-[#f5f5dc]/60" />
              </button>
            </div>
          </footer>
        </main>
      </div>

      {/* Trailer Modal */}
      {showTrailer && movie?.trailerUrl && (
        <TrailerModal trailerUrl={movie.trailerUrl} onClose={() => setShowTrailer(false)} />
      )}
    </div>
  );
};

// Cinema List Item Component
const CinemaListItem = ({ cinema, movieTitle, isSelected, onClick }) => {
  const [logoError, setLogoError] = useState(false);

  // Get the ticket URL for a showtime (prioritize session URL, then cinema URL, then website)
  const getTicketUrl = (showtime) => {
    return showtime?.ticketUrl || cinema.ticketUrl || cinema.website || cinema.googleMapsUrl || '#';
  };

  return (
    <div 
      className={`p-3 sm:p-4 cursor-pointer transition-colors ${isSelected ? 'bg-[#50c878]/10' : 'hover:bg-[#50c878]/5'}`}
      onClick={onClick}
      data-testid={`cinema-item-${cinema.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2e4a2a] rounded-lg flex items-center justify-center flex-shrink-0">
          {!logoError && cinema.logo ? (
            <img 
              src={cinema.logo} 
              alt={cinema.name}
              className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#50c878]" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-[#f5f5dc] font-semibold text-sm sm:text-base leading-tight">{cinema.name}</h3>
            <div className="flex items-center gap-1 sm:gap-2 text-[#f5f5dc]/50 text-xs sm:text-sm flex-shrink-0">
              <span className="text-[#50c878] font-bold whitespace-nowrap">{cinema.formattedDistance}</span>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </div>
          </div>
          
          <p className="text-[#f5f5dc]/40 text-xs sm:text-sm mb-1 truncate">{cinema.address}</p>
          <p className="text-[#f5f5dc]/60 text-xs sm:text-sm mb-2 sm:mb-3">{movieTitle || 'Película'}</p>
          
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {/* Ticket Only Mode - Just show "Buy Tickets" button */}
            {cinema.ticketOnlyMode && cinema.ticketUrl ? (
              <a
                href={cinema.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent('InitiateCheckout', { content_name: cinema.name, content_category: 'ticket_purchase' });
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 sm:gap-2 bg-[#50c878] text-white hover:bg-[#40a0a0]"
                data-testid={`buy-tickets-${cinema.id}`}
              >
                <span>Comprar</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              /* Normal Mode - Show showtimes */
              cinema.showtimes?.map((showtime) => (
                <a
                  key={showtime.id}
                  href={getTicketUrl(showtime)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackEvent('InitiateCheckout', {
                      content_name: cinema.name,
                      content_category: 'ticket_purchase',
                      content_ids: [showtime.id],
                      value: showtime.time,
                      currency: 'EUR'
                    });
                  }}
                  className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-2 ${
                    showtime.available
                      ? 'bg-[#50c878] text-white hover:bg-[#40a0a0]'
                      : 'bg-[#2e4a2a] text-[#f5f5dc]/30 cursor-not-allowed'
                  }`}
                  data-testid={`showtime-${showtime.id}`}
                >
                  {showtime.time ? (
                    <>
                      <span>{showtime.time}</span>
                      <span className="text-xs opacity-75 hidden sm:inline">({showtime.format})</span>
                    </>
                  ) : (
                    <span>Comprar</span>
                  )}
                  {showtime.available && <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
                </a>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
